import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FieldDropdown, useRemoteOptions } from './pickers.jsx';
import { activeDataFilters, cloneDataFilterState, dataFilterDefinitions, PIVOT_FILTER_OPERATORS, validDataFilterState } from '../filterLogic.js';
// Preserve existing export imports while pivots use the same editor and saved state shape.
export { activeDataFilters as activeExportFilters, dataFilterDefinitions as exportFilterDefinitions } from '../filterLogic.js';


function FilterTextDropdown({ label, options, value, onChange, loadOptions, loadKey }) {
  const [open, setOpen] = useState(false);
  const [menuLayout, setMenuLayout] = useState({ above: false, height: 360 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const close = () => { setOpen(false); triggerRef.current?.focus(); };
  const remote = useRemoteOptions(open, value, loadOptions, loadKey);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const position = () => {
      const bounds = triggerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const flip = below < 260 && above > below;
      setMenuLayout({ above: flip, height: Math.max(160, Math.min(360, flip ? above : below)),
        top: flip ? bounds.top - 4 : bounds.bottom + 4, left: bounds.left, width: bounds.width });
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => { window.removeEventListener('resize', position); window.removeEventListener('scroll', position, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = event => { if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const visible = (loadOptions ? remote.options : options).filter(option => option.label.toLowerCase().includes(value.trim().toLowerCase()));
  return <div className="reach-field-dropdown" ref={rootRef} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    <button type="button" ref={triggerRef} className="o-control reach-field-trigger" aria-label={label}
      aria-haspopup="dialog" aria-controls={menuId} aria-expanded={open} onClick={() => setOpen(current => !current)}>
      <span>{value || 'Choose or type a value'}</span><span aria-hidden="true">⌄</span>
    </button>
    {open && createPortal(<div ref={menuRef} className="o-popover reach-field-menu reach-export-text-menu" id={menuId} role="dialog" aria-label={label}
      data-reach-export-menu="true" style={{ position: 'fixed', zIndex: 160, width: menuLayout.width, left: menuLayout.left,
        maxHeight: menuLayout.height, top: menuLayout.above ? 'auto' : menuLayout.top,
        bottom: menuLayout.above ? window.innerHeight - menuLayout.top : undefined }}>
      <input type="text" className="o-control" value={value} autoFocus aria-label={`Type ${label.toLowerCase()}`}
        placeholder="Type any value…" onChange={event => onChange(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); close(); } }} />
      <p className="o-note">Type a value, or choose an existing value. Press Enter to close.</p>
      <div className="reach-field-options" role="listbox" aria-label={`Existing ${label.toLowerCase()}`}>
        {visible.slice(0, 100).map(option => <div key={option.id} role="option" aria-selected={value === option.label}
          tabIndex={0} className="reach-field-option reach-export-value-option"
          onClick={() => { onChange(option.label); close(); }}
          onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); onChange(option.label); close(); } }}>
          {option.label}
        </div>)}
        {remote.loading && <p className="o-note" role="status">Loading values…</p>}
        {remote.error && <p className="o-note" role="alert">{remote.error} Your typed value can still be used.</p>}
        {remote.next && <button type="button" className="o-btn o-btn-sm" disabled={remote.loading} onClick={remote.more}>Load more values</button>}
        {!visible.length && !remote.loading && <p className="o-note">No existing values match. Your typed value can still be used.</p>}
      </div>
    </div>, document.body)}
  </div>;
}


function DataFilterDialog({ definitions, streams, initial, context, title, onClose, onSave, loadValues }) {
  const [draft, setDraft] = useState(() => cloneDataFilterState(initial));
  const initialIds = activeDataFilters(definitions, streams, initial).map(item => item.id);
  const [chosen, setChosen] = useState(() => [...new Set(['stream', ...initialIds])]);
  const [editing, setEditing] = useState(initialIds[0] || (context === 'pivot' ? 'stream' : null));
  const dialogRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('select')?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  useEffect(() => {
    if (document.activeElement === document.body) dialogRef.current?.querySelector('select')?.focus();
  }, [editing]);
  const updatePart = (key, id, value) => setDraft(current => ({ ...current, [key]: { ...current[key], [id]: value } }));
  const reset = id => setDraft(current => ({ ...current,
    selectedStreamIds: id === 'stream' ? streams.map(item => item.id) : current.selectedStreamIds,
    dimensionFilters: { ...current.dimensionFilters, [id]: [] },
    filterOperators: { ...current.filterOperators, [id]: 'list' }, filterText: { ...current.filterText, [id]: '' } }));
  const remove = id => { reset(id); setChosen(current => current.filter(value => value !== id)); setEditing(null); };
  const changeDimension = next => {
    reset(editing); setChosen(current => current.map(id => id === editing ? next : id)); setEditing(next);
  };
  const definition = definitions.find(item => item.id === editing);
  const operator = draft.filterOperators[editing] || 'list';
  const needsValue = PIVOT_FILTER_OPERATORS.find(item => item.id === operator)?.needsValue;
  const available = definitions.filter(item => !chosen.includes(item.id));
  const valid = validDataFilterState(draft, chosen);
  const loader = loadValues && definition && editing !== 'stream' && !definition.calculated ? (search, after) => loadValues(editing, search, after, draft.selectedStreamIds) : undefined;
  const loadKey = JSON.stringify([editing, draft.selectedStreamIds]);
  const keyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.key === 'Tab') {
      const roots = [dialogRef.current, ...document.querySelectorAll('[data-reach-export-menu]')];
      const nodes = roots.flatMap(root => [...root.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')])
        .filter(node => node.getClientRects().length);
      if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes.at(-1)?.focus(); }
      else if (!event.shiftKey && document.activeElement === nodes.at(-1)) { event.preventDefault(); nodes[0]?.focus(); }
    }
  };
  return createPortal(<div className="o-scrim" role="presentation" onClick={onClose}>
    <div className="o-modal reach-export-filter-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}
      ref={dialogRef} onClick={event => event.stopPropagation()} onKeyDown={keyDown}>
      <div className="o-modal-head"><div><h2 id={titleId}>{title}</h2><p className="o-note">Conditions are combined with AND.</p></div>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label={`Close ${context} filters`} onClick={onClose}>×</button></div>
      <div className="o-modal-body reach-export-filter-body">
        <aside className="reach-export-filter-list" aria-label="Configured filters"><span className="o-field-lbl">Filters</span>
          {!chosen.length && <p className="o-note">No filters yet.</p>}
          {chosen.map((id, index) => {
            const item = definitions.find(value => value.id === id);
            if (!item) return null;
            return <div key={id}>{index > 0 && <div className="reach-export-filter-and">AND</div>}
              <button type="button" className="o-side-item reach-export-filter-item" aria-current={editing === id ? 'true' : undefined}
                aria-label={`Edit ${item.label} filter`} onClick={() => setEditing(id)}>
                <span>{item.label}</span><span aria-hidden="true">›</span>
              </button></div>;
          })}
        </aside>
        <div className="reach-export-filter-editor">
          {!definition ? <>
            <label><span className="o-field-lbl">Add a filter</span><select className="o-control" aria-label={`Select ${context} filter field`} value=""
              disabled={!available.length} onChange={event => { const next = event.target.value; setChosen(current => [...current, next]); setEditing(next); }}>
              <option value="">Select fields…</option>{available.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select></label>
            <div className="o-state reach-export-filter-empty"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M3 4h18l-7 8v7l-4 2v-9Z" /></svg>
              <b>Add a new filter</b><span>Choose a field above to set its condition and values.</span></div>
          </> : <>
            <div className="reach-export-filter-editor-head"><label><span className="o-field-lbl">Filter dimension</span>
              <select className="o-control" aria-label={`Filter dimension: ${definition.label}`} value={editing} onChange={event => changeDimension(event.target.value)}>
                {definitions.map(item => <option key={item.id} value={item.id} disabled={item.id !== editing && chosen.includes(item.id)}>{item.label}</option>)}
              </select></label>
              <button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label={`Remove ${definition.label} filter`} onClick={() => remove(editing)}>Remove filter</button></div>
            <label className="reach-export-filter-condition"><span className="o-field-lbl">Condition</span>
              <select className="o-control" aria-label={`Condition: ${definition.label}`} value={operator}
                onChange={event => updatePart('filterOperators', editing, event.target.value)}>
                {PIVOT_FILTER_OPERATORS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select></label>
            <div><span className="o-field-lbl">Value</span>{operator === 'list'
              ? <FieldDropdown key={editing} label={`Filter values: ${definition.label}`} compact chips placeholder="Select values…" options={definition.values}
                loadOptions={loader} loadKey={loadKey}
                selected={editing === 'stream' ? draft.selectedStreamIds : draft.dimensionFilters[editing] || []}
                emptyLabel={editing === 'stream' ? 'No streams selected' : `All ${definition.label.toLowerCase()} values`}
                emptyHint={editing === 'stream' ? 'Select at least one data stream.' : 'No restriction — all values are included.'}
                onChange={values => editing === 'stream' ? setDraft(current => ({ ...current, selectedStreamIds: values })) : updatePart('dimensionFilters', editing, values)} />
              : needsValue ? <FilterTextDropdown key={editing} label={`Filter value: ${definition.label}`} options={definition.values}
                loadOptions={loader} loadKey={loadKey}
                value={draft.filterText[editing] || ''} onChange={value => updatePart('filterText', editing, value)} />
                : <select className="o-control" aria-label={`Filter value: ${definition.label}`} disabled><option>No value required</option></select>}
              {operator === 'list' && !loader && !definition.values.length && <p className="o-note">No values loaded. Use a text condition to enter a value.</p>}
            </div>
          </>}
          {!valid && <p className="o-note" role="status">{!draft.selectedStreamIds.length ? 'Choose at least one data stream.' : 'Enter a value for each text condition before saving.'}</p>}
        </div>
      </div>
      <div className="o-modal-foot"><button type="button" className="o-btn o-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="o-btn" disabled={!available.length} onClick={() => setEditing(null)}>+ Add new filter</button>
        <button type="button" className="o-btn o-btn-primary" disabled={!valid} onClick={() => onSave(draft)}>Save filters</button></div>
    </div>
  </div>, document.body);
}

export function DataFilters({ rows, streams, dimensionFields, selectedStreamIds, setSelectedStreamIds,
  dimensionFilters = {}, setDimensionFilters, filterOperators = {}, setFilterOperators, filterText = {}, setFilterText,
  context = 'export', title = 'Filters', loadValues }) {
  const [open, setOpen] = useState(false);
  const definitions = dataFilterDefinitions(rows, streams, dimensionFields);
  const state = { selectedStreamIds, dimensionFilters, filterOperators, filterText };
  const active = activeDataFilters(definitions, streams, state);
  return <div className="reach-export-filters">
    <span className="o-field-lbl">Filters</span>
    <button type="button" className="o-control reach-field-trigger reach-export-filter-trigger" aria-label={`Open ${context} filters`}
      aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
      <span>{active.length ? `${active.length} ${active.length === 1 ? 'filter' : 'filters'} applied` : 'Add filters…'}</span><span aria-hidden="true">⌄</span>
    </button>
    <p className="o-note">{active.length ? active.map(item => item.label).join(' · AND · ') : 'No filters applied.'}</p>
    {open && <DataFilterDialog definitions={definitions} streams={streams} initial={state} context={context} title={title} loadValues={loadValues} onClose={() => setOpen(false)} onSave={next => {
      setSelectedStreamIds(next.selectedStreamIds); setDimensionFilters(next.dimensionFilters);
      setFilterOperators(next.filterOperators); setFilterText(next.filterText); setOpen(false);
    }} />}
  </div>;
}

export function ExportFilters(props) {
  return <DataFilters {...props} context="export" />;
}
